import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import authService from '../services/authService';
import userService, { type UpdateUserPayload, type UserRecord } from '../services/userService';

export interface User {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  organization_id: number | null;
  locale?: string;
  must_change_password?: boolean;
}

interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  token: string | null;
  loading: boolean;
  error: string | null;
  mustChangePassword: boolean;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  loading: false,
  error: null,
  mustChangePassword: false,
};

export const login = createAsyncThunk(
  'auth/login',
  async ({ username, password }: { username: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authService.login(username, password);
      return response;
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Login failed';
      return rejectWithValue(msg);
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'auth/updateProfile',
  async ({ userId, payload }: { userId: number; payload: UpdateUserPayload }, { rejectWithValue }) => {
    try {
      const response = await userService.updateUser(userId, payload);
      return response;
    } catch (error: unknown) {
      const msg = (error as { response?: { data?: { detail?: string } } }).response?.data?.detail || 'Failed to update profile';
      return rejectWithValue(msg);
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.error = null;
      state.mustChangePassword = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    passwordChanged: (state) => {
      state.mustChangePassword = false;
      if (state.user) {
        state.user.must_change_password = false;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<{ user: User; access_token: string }>) => {
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.access_token;
        state.loading = false;
        state.mustChangePassword = action.payload.user.must_change_password || false;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserProfile.fulfilled, (state, action: PayloadAction<UserRecord>) => {
        if (state.user && state.user.id === action.payload.id) {
          state.user = { ...state.user, ...action.payload };
        }
      });
  },
});

export const { logout, clearError, passwordChanged } = authSlice.actions;
export default authSlice.reducer;